import React, { useRef, useEffect, useMemo, useCallback } from "react";
import anime from "animejs";
import { attemptFullscreenToggle } from "../../../../utilities";
import { Sprite, Text, Graphics, Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import { TextStyle } from "pixi.js";
import { SOUND_NAME } from "../../../../sounds";
import { getNewUITexture } from "../../../../assets";
import ButtonShadow from "./ButtonShadow";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { FONT_CONFIG } from "../../../../pixiSettings";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { useDispatch, useSelector } from "react-redux";
import { useButtonBrightness } from "../../../../Hooks/useButtonBrightness";
import { triggerScalePress } from "../../../../Hooks/useButtonHover";
import {
  openAutoplayPopup,
  closeAutoplayPopup,
  stopAutoPlay,
  closeMenu,
} from "../../../../State/actions";
import {
  selectAutoPlayActive,
  selectRemainingAutoSpins,
  selectIsSpinning,
  selectAutoplayPopupActive,
  selectCriticalAnimationPlaying,
  selectAnyFeatureTriggeredOrActive,
  selectReplaySessionActive,
} from "../../../../State/selectors";

export const AUTOPLAY_BUTTON_CONFIG = {
  // Button base dimensions
  BASE_WIDTH: 80,
  BASE_HEIGHT: 80,

  // Background base dimensions
  BACKGROUND_BASE_WIDTH: 80,
  BACKGROUND_BASE_HEIGHT: 152,

  // An iPad upright (layout TABLET_PORTRAIT on the MOBILE device, 2026-10-29):
  // a touch bigger and a bit further from the spin button, on their ruler.
  LAYOUT_CONFIGS: {
    // TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.1 },
    // TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.09 }, // a notch closer
    // TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.09, gapAboveToolbar: 0.14 }, // a tiny bit up
    // TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.09, gapAboveToolbar: 0.16 }, // a bit more up
    // TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.09, gapAboveToolbar: 0.19 }, // more
    // TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.09, gapAboveToolbar: 0.21 }, // 2 notches more
    // TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.09, gapAboveToolbar: 0.23 }, // a bit more
    // TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.09, gapAboveToolbar: 0.25 }, // 2 more
    // TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.09, gapAboveToolbar: 0.27 },
    TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.09, gapAboveToolbar: 0.29 }, // matched on their ruler (2026-10-29)
  },
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.03,
        backgroundMultiplier: 1.5,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.575,
        backgroundMultiplier: 1.325,
      },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.04,
        backgroundMultiplier: 1.5,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.45,
        backgroundMultiplier: 1.325,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.3,
        backgroundMultiplier: 1.75,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.7,
        backgroundMultiplier: 1.275,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
      backgroundMultiplier: 0.8,
    },
  },
};

const ButtonAutoplay = ({ toolbar }) => {
  const dispatch = useDispatch();
  const {
    getResponsiveConfig,
    device,
    orientation,
    dimensions,
    uiSymbolScale,
    isMobileSmall,
  } = useDimensions();

  // Redux state
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const remainingAutoSpins = useSelector(selectRemainingAutoSpins);
  const popupActive = useSelector(selectAutoplayPopupActive);
  const isSpinning = useSelector(selectIsSpinning);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const anyFeatureTriggeredOrActive = useSelector(
    selectAnyFeatureTriggeredOrActive,
  );
  const replaySessionActive = useSelector(selectReplaySessionActive);
  const backgroundSpriteRef = useRef(null);
  const backgroundAnimationRef = useRef(null);
  const widthAnimationRef = useRef(null);
  const textRef = useRef(null);
  const previousIsSpinningRef = useRef(isSpinning);

  // Add ref to track if button is currently processing a click
  const isProcessingClickRef = useRef(false);

  // Add ref to store timeout ID for proper cleanup
  const debounceTimeoutRef = useRef(null);

  // Ref for circle background — hover applies brightness to both circle + icon
  const circleBgRef = useRef(null);
  const buttonContainerRef = useRef(null);
  const brightnessFilterCircle = useRef(null);
  if (!brightnessFilterCircle.current) {
    brightnessFilterCircle.current = new PIXI.ColorMatrixFilter();
    brightnessFilterCircle.current.brightness(1.35, false);
    brightnessFilterCircle.current.resolution = window.devicePixelRatio || 1;
    brightnessFilterCircle.current.padding = 8;
  }

  const textureInactive = getNewUITexture("icon_autoplay");
  const textureActive = getNewUITexture("icon_autoplay_stop");
  const textureCircle = getNewUITexture("icon_autoplay_background");
  const textureBackground = getNewUITexture("icon_autoplay_extended");

  // Use active texture when autoplay is active, otherwise use inactive
  const texture = autoPlayActive ? textureActive : textureInactive;

  // Disable button when: critical animation is playing OR (spin is active AND autoplay is not active) OR hold and win is active OR replay is active
  const isDisabled =
    criticalAnimationPlaying ||
    (isSpinning && !autoPlayActive) ||
    anyFeatureTriggeredOrActive ||
    replaySessionActive;

  // Shared hover/press hook
  const {
    buttonRef,
    handlePointerOver: basePointerOver,
    handlePointerOut: basePointerOut,
    cleanupFilters,
    alpha,
    cursor,
  } = useButtonBrightness({ disabled: isDisabled });

  const handlePointerOver = useCallback(() => {
    basePointerOver();
    if (isDisabled) return;
    const bg = circleBgRef.current;
    if (bg && (!bg.filters || bg.filters.length === 0)) {
      bg.filters = [brightnessFilterCircle.current];
    }
  }, [basePointerOver, isDisabled]);

  const handlePointerOut = useCallback(() => {
    basePointerOut();
    const bg = circleBgRef.current;
    if (bg) bg.filters = [];
  }, [basePointerOut]);

  // Auto-clear circle bg filters when disabled mid-hover
  useEffect(() => {
    if (isDisabled && circleBgRef.current) {
      circleBgRef.current.filters = [];
    }
  }, [isDisabled]);

  // Get responsive configuration from context - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () => getResponsiveConfig(AUTOPLAY_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Calculate background config — match button width, derive height from texture aspect ratio
  const buttonBackgroundConfig = useMemo(() => {
    const aspect = textureBackground
      ? textureBackground.height / textureBackground.width
      : 280 / 179;
    const pillWidth = buttonConfig.width - 2;
    const pillHeight = pillWidth * aspect;

    return {
      width: pillWidth,
      height: pillHeight,
    };
  }, [buttonConfig.width, textureBackground]);

  // Get text configuration (font multiplier, x/y offsets relative to button center)
  const getTextConfig = () => {
    let fontMultiplier = 1.0;
    let x = 0;
    let y = 0;

    // The pill extends below in portrait, to the left in landscape
    // Text sits in the extended area (bottom half in portrait, left half in landscape)
    const pillExtraHeight = buttonBackgroundConfig.height - buttonConfig.width; // extra area beyond circle

    if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
      // Text centered horizontally, in the extended area below the circle
      x = 0;
      y = buttonConfig.width / 2 + pillExtraHeight * 0.35;
      if (device === PixiSettings.DEVICE.MOBILE) fontMultiplier = 1.6;
      else if (device === PixiSettings.DEVICE.TABLET) fontMultiplier = 1.85;
      else fontMultiplier = 1.0;
    } else {
      // Landscape/Desktop: pill rotated 90°, extends to the left
      // Text centered vertically, in the extended area to the left of the circle
      x = -(buttonConfig.width / 2 + pillExtraHeight * 0.35);
      y = 0;
      if (device === PixiSettings.DEVICE.MOBILE) fontMultiplier = 1.45;
      else if (device === PixiSettings.DEVICE.TABLET) fontMultiplier = 1.4;
      else fontMultiplier = 1.0;
    }

    return { fontMultiplier, x, y };
  };

  // Calculate responsive button position based on device and orientation - memoized
  // Uses toolbar width instead of full screen width
  const buttonPosition = useMemo(() => {
    if (isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          button: {
            x: toolbar.width / 2 - buttonConfig.width * (buttonConfig.sideOffset ?? 1.875),
            y:
              -toolbar.visualHeight -
              buttonConfig.height -
              buttonConfig.height * (buttonConfig.gapAboveToolbar ?? 0.125),
          },
          background: {
            x:
              toolbar.width / 2 -
              buttonConfig.width *
                (buttonConfig.sideOffset != null ? buttonConfig.sideOffset + 0.16 : 2.035),
            y:
              -toolbar.visualHeight -
              buttonConfig.height -
              buttonConfig.height *
                (buttonConfig.gapAboveToolbar != null ? buttonConfig.gapAboveToolbar - 0.025 : 0.1),
          },
        };
      } else {
        return {
          button: {
            x: toolbar.width - buttonConfig.width * 0.775,
            y: -dimensions.height / 2 + buttonConfig.height * 2.845,
          },
          background: {
            x: toolbar.width - buttonConfig.width * 0.5,
            y: -dimensions.height / 2 + buttonConfig.height * 2.47,
          },
        };
      }
    } else if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          button: {
            x: toolbar.width / 2 - buttonConfig.width * 1.8,
            y:
              -toolbar.visualHeight -
              buttonConfig.height -
              buttonConfig.height * 0.25,
          },
          background: {
            x: toolbar.width / 2 - buttonConfig.width * 1.96,
            y:
              -toolbar.visualHeight -
              buttonConfig.height -
              buttonConfig.height * 0.225,
          },
        };
      } else {
        return {
          button: {
            x: toolbar.width - buttonConfig.width * 0.52,
            y: -dimensions.height / 2 + buttonConfig.height * 2.825,
          },
          background: {
            x: toolbar.width - buttonConfig.width * 0.5,
            y: -dimensions.height / 2 + buttonConfig.height * 2.275,
          },
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          button: {
            x: toolbar.width / 2 - buttonConfig.width * 1.85,
            y: -dimensions.height / 5.1,
          },
          background: {
            x: toolbar.width / 2 - buttonConfig.width * 2.057,
            y: -dimensions.height / 4.5,
          },
        };
      } else {
        return {
          button: {
            x: toolbar.width - buttonConfig.width * 3.15,
            y: toolbar.height / 2,
          },
          background: {
            x: toolbar.width - buttonConfig.width * 2.9,
            y: toolbar.height / 2 - buttonBackgroundConfig.height * 0.265,
          },
        };
      }
    } else {
      const desktopY = toolbar.height / 2 - buttonConfig.height * 0.2;
      return {
        button: {
          x: toolbar.width - buttonConfig.width * 4.4,
          y: desktopY,
        },
        background: {
          x: toolbar.width - buttonConfig.width * 4.1,
          y: desktopY - buttonBackgroundConfig.height * 0.265,
        },
      };
    }
  }, [
    buttonConfig.gapAboveToolbar,
    buttonConfig.sideOffset,
    device,
    orientation,
    isMobileSmall,
    dimensions.height,
    toolbar.width,
    toolbar.height,
    toolbar.visualHeight,
    buttonConfig.width,
    buttonConfig.height,
    buttonBackgroundConfig.height,
  ]);

  // Calculate background rotation: 0 for portrait, 90deg for landscape/desktop
  const backgroundRotation =
    orientation === PixiSettings.ORIENTATION.PORTRAIT ? 0 : Math.PI / 2;

  // Remaining autospins count from Redux
  const isInfinity = remainingAutoSpins === Infinity;
  const displayRemainingSpins = isInfinity
    ? null
    : remainingAutoSpins.toString();

  // Get infinity icon texture
  const infinityTexture = getNewUITexture("icon_infinity");

  // Text configuration
  const textConfig = getTextConfig();
  const baseFontSize = 19;
  const responsiveFontSize = baseFontSize * textConfig.fontMultiplier;
  const FONT_SIZE = Math.round((responsiveFontSize * uiSymbolScale) / 2) * 2;

  // Calculate text/icon position relative to button center
  const textPosition = {
    x: buttonPosition.button.x + textConfig.x,
    y: buttonPosition.button.y + textConfig.y,
  };

  // Calculate infinity icon size maintaining aspect ratio
  const infinityIconSize = useMemo(() => {
    if (!isInfinity || !infinityTexture) return { width: 0, height: 0 };

    const iconAspectRatio = infinityTexture.width / infinityTexture.height;
    const maxSize = FONT_SIZE * 1.2;

    // Use maxSize as width, calculate height to maintain aspect ratio
    const iconWidth = maxSize;
    const iconHeight = iconWidth / iconAspectRatio;

    return { width: iconWidth, height: iconHeight };
  }, [isInfinity, infinityTexture, FONT_SIZE]);

  // Text style for remaining autospins
  const dpr = window.devicePixelRatio || 1;
  const autospinsTextStyle = new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: FONT_SIZE,
    fontWeight: "700",
    fill: "#ffffff",
    align: "center",
    resolution: dpr,
  });

  const handleClick = () => {
    // Disable button when disabled
    if (isDisabled) {
      return;
    }

    // Close menu if open
    dispatch(closeMenu());

    // Attempt to toggle fullscreen if conditions are met
    attemptFullscreenToggle(dispatch, device);

    // Prevent rapid clicks if already processing
    if (isProcessingClickRef.current) {
      return;
    }

    // Clear any existing timeout before starting new one
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;

    soundManager.play(SOUND_NAME.button_click);

    // Add press animation to entire button container (circle + icon together)
    if (buttonContainerRef.current) {
      triggerScalePress(buttonContainerRef.current);
    }

    if (autoPlayActive) {
      // If autoplay is active, stop it
      dispatch(stopAutoPlay());
    } else {
      // If autoplay is inactive, toggle popup
      if (popupActive) {
        dispatch(closeAutoplayPopup());
      } else {
        dispatch(openAutoplayPopup());
      }
    }

    // Reset processing flag after animation completes (500ms debounce)
    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  // Sync cached dimensions whenever buttonBackgroundConfig changes (resize/orientation)
  useEffect(() => {
    if (!backgroundSpriteRef.current) return;

    const bg = backgroundSpriteRef.current;

    // First mount: set initial collapsed height
    if (!bg._initialized) {
      bg.height = 0;
      bg.alpha = 1;
      bg._initialized = true;
    }

    // Always update cached dimensions so pulse animation uses correct target
    bg._originalHeight = buttonBackgroundConfig.height;
    bg._originalWidth = buttonBackgroundConfig.width;

    // If autoplay is running, snap to new size immediately (no stale shrink)
    if (autoPlayActive) {
      bg.height = buttonBackgroundConfig.height;
    }

    if (textRef.current && !textRef.current._initialized) {
      textRef.current.alpha = 1;
      textRef.current._initialized = true;
    }
  }, [
    buttonBackgroundConfig.height,
    buttonBackgroundConfig.width,
    autoPlayActive,
  ]);

  // Animate background height and alpha based on autoPlayActive state
  // Only expand background when autoplay is running, not when popup opens
  useEffect(() => {
    if (!backgroundSpriteRef.current || !textRef.current) return;

    const backgroundSprite = backgroundSpriteRef.current;
    const textElement = textRef.current;

    // _originalHeight is always kept in sync by the init effect above
    // No need to check/set it here

    // Stop any existing animation
    if (backgroundAnimationRef.current) {
      backgroundAnimationRef.current.pause();
    }

    // Only expand background when autoplay is active (running)
    const targetHeight = autoPlayActive ? buttonBackgroundConfig.height : 0;
    const isClosing = !autoPlayActive;

    // Animate height
    anime({
      targets: backgroundSprite,
      height: targetHeight,
      duration: 300,
      easing: "easeOutCubic",
    });

    // Animate alpha - background and text have separate timings
    if (isClosing) {
      // Background fades out with delay
      anime({
        targets: backgroundSprite,
        alpha: 0,
        delay: 35,
        duration: 20,
        easing: "easeOutCubic",
      });
      // Text fades out instantly (no delay)
      anime({
        targets: textElement,
        alpha: 0,
        delay: 0,
        duration: 20,
        easing: "easeOutCubic",
      });
    } else {
      // Fade in when opening - set initial alpha via ref, not prop
      requestAnimationFrame(() => {
        if (backgroundSprite) backgroundSprite.alpha = 0;
        if (textElement) textElement.alpha = 0;
      });
      // Background fades in with delay
      anime({
        targets: backgroundSprite,
        alpha: 1,
        delay: 25,
        duration: 100,
        easing: "easeOutCubic",
      });
      // Text fades in with delay (appears after background)
      anime({
        targets: textElement,
        alpha: 1,
        delay: 100,
        duration: 100,
        easing: "easeOutCubic",
      });
    }
  }, [autoPlayActive, buttonBackgroundConfig.height]);

  // Animate background height when a new spin starts during autoplay
  useEffect(() => {
    // Only animate if autoplay is active and spin just started (transitioned from false to true)
    if (
      !autoPlayActive ||
      !backgroundSpriteRef.current ||
      !isSpinning ||
      previousIsSpinningRef.current === isSpinning
    ) {
      previousIsSpinningRef.current = isSpinning;
      return;
    }

    const backgroundSprite = backgroundSpriteRef.current;
    const originalHeight =
      backgroundSprite._originalHeight || buttonBackgroundConfig.height;

    // Stop any existing height animation
    if (widthAnimationRef.current) {
      widthAnimationRef.current.pause();
    }

    // Animate: shrink height then bounce back with elastic easing
    widthAnimationRef.current = anime({
      targets: backgroundSprite,
      height: [originalHeight * 0.8, originalHeight],
      duration: 400,
      easing: "easeOutElastic(1, 0.5)",
    });

    // Update previous value
    previousIsSpinningRef.current = isSpinning;
  }, [isSpinning, autoPlayActive, buttonBackgroundConfig.height]);

  // Clean up on unmount
  useEffect(() => {
    const circleBg = circleBgRef.current;
    return () => {
      cleanupFilters();
      if (circleBg) circleBg.filters = [];
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [cleanupFilters]);

  // Calculate button radius to match the visual sprite exactly - memoized
  const buttonRadius = useMemo(
    () => buttonConfig.width / 2,
    [buttonConfig.width],
  );

  return (
    <Container name="secondary_button_autoplay">
      {/* Soft drop shadow under it (ButtonShadow, utils/buttonShadow) */}
      <ButtonShadow target={backgroundSpriteRef} texture={textureBackground} width={buttonBackgroundConfig.width} height={buttonBackgroundConfig.height} />
      <Sprite
        ref={backgroundSpriteRef}
        texture={textureBackground}
        name="button_autoplay_pill"
        anchor={{
          x: 0.5,
          y:
            textureCircle && textureBackground
              ? textureCircle.height / 2 / textureBackground.height
              : 90 / 280,
        }}
        width={buttonBackgroundConfig.width}
        height={buttonBackgroundConfig.height}
        x={buttonPosition.button.x}
        y={buttonPosition.button.y}
        rotation={backgroundRotation}
        roundPixels={true}
      />
      {isInfinity && infinityTexture ? (
        <Sprite
          ref={textRef}
          texture={infinityTexture}
          anchor={0.5}
          width={infinityIconSize.width}
          height={infinityIconSize.height}
          x={textPosition.x}
          y={textPosition.y}
          roundPixels={true}
        />
      ) : (
        <Text
          ref={textRef}
          text={displayRemainingSpins}
          style={autospinsTextStyle}
          anchor={0.5}
          x={textPosition.x}
          y={textPosition.y}
          roundPixels={true}
        />
      )}
      <Container
        ref={buttonContainerRef}
        x={buttonPosition.button.x}
        y={buttonPosition.button.y}
      >
        {/* Interactive circular area - handles all pointer events */}
        <Graphics
          name="button_autoplay_interactive_area"
          eventMode="static"
          cursor={cursor}
          hitArea={new PIXI.Circle(0, 0, buttonRadius)}
          onpointertap={handleClick}
          onpointerover={handlePointerOver}
          onpointerout={handlePointerOut}
          draw={(g) => {
            g.clear();
            // Nearly invisible fill (required for hit detection to work)
            g.beginFill(0x000000, 0.01);
            g.drawCircle(0, 0, buttonRadius);
            g.endFill();
          }}
        />

        {/* Always-visible circle background */}
        {/* Soft drop shadow under it (ButtonShadow, utils/buttonShadow) */}
        <ButtonShadow target={circleBgRef} texture={textureCircle} width={buttonConfig.width} height={buttonConfig.height} />
        <Sprite
          ref={circleBgRef}
          texture={textureCircle}
          name="button_autoplay_circle_bg"
          anchor={0.5}
          width={buttonConfig.width}
          height={buttonConfig.height}
          x={0}
          y={0}
          eventMode="none"
          alpha={alpha}
          roundPixels={true}
        />

        {/* Visual sprite (non-interactive, just for display) */}
        <Sprite
          ref={buttonRef}
          texture={texture}
          name="button_autoplay_icon"
          anchor={0.5}
          width={buttonConfig.width}
          height={buttonConfig.height}
          x={0}
          y={0}
          eventMode="none"
          alpha={alpha}
          roundPixels={true}
        />
      </Container>
    </Container>
  );
};

export default ButtonAutoplay;
